import { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import {
  Target, CheckSquare, RefreshCw, BarChart2, Send, Sparkles,
  Bot, User, Loader2, ChevronDown, ChevronRight, Zap,
  FileText, Rocket, Calendar, Flag
} from "lucide-react";

// ─── Types ─────────────────────────────────────────────────────────────────

interface PlanGoal {
  type: "goal" | "food_goal" | "fitness_goal";
  title: string;
  description: string;
  category: string;
  deadline: string | null;
  targetValue: string | null;
  priority: "low" | "medium" | "high";
  goalSource: string | null;
  healthMetric: string | null;
}

interface PlanTask {
  title: string;
  description: string;
  dueDate: string | null;
  priority: "low" | "medium" | "high";
  category: string;
  subtasks: { text: string }[];
}

interface PlanHabit {
  name: string;
  description: string;
  category: string;
  frequency: string;
  target: number;
  habitType: string;
}

interface PlanTracker {
  name: string;
  valueType: string;
  unitLabel: string;
  period: string;
  goalValue: number | null;
  category: string;
}

interface PlanData {
  summary: string;
  goal: PlanGoal;
  tasks: PlanTask[];
  habits: PlanHabit[];
  trackers: PlanTracker[];
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface CreatePlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationHistory: { role: "user" | "assistant"; content: string }[];
  onPostToNotepad: (text: string) => void;
}

// ─── Priority badge ─────────────────────────────────────────────────────────

function PriorityBadge({ priority }: { priority: string }) {
  const cls =
    priority === "high" ? "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" :
    priority === "medium" ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300" :
    "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300";
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cls}`}>{priority}</span>;
}

// ─── Goal type badge ────────────────────────────────────────────────────────

function GoalTypeBadge({ type }: { type: string }) {
  if (type === "food_goal") return <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900 border-0 text-xs">🍎 Food Goal</Badge>;
  if (type === "fitness_goal") return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 border-0 text-xs">💪 Fitness Goal</Badge>;
  return <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900 border-0 text-xs">🎯 Goal</Badge>;
}

// ─── Task item with expand ──────────────────────────────────────────────────

function TaskItem({ task }: { task: PlanTask }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border rounded-lg overflow-hidden">
      <div
        className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-muted/40 transition-colors"
        onClick={() => task.subtasks.length > 0 && setExpanded(v => !v)}
      >
        {task.subtasks.length > 0 ? (
          expanded ? <ChevronDown className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
        ) : (
          <CheckSquare className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium">{task.title}</span>
            <PriorityBadge priority={task.priority} />
          </div>
          {task.description && <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{task.description}</p>}
          {task.dueDate && (
            <div className="flex items-center gap-1 mt-0.5">
              <Calendar className="h-3 w-3 text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground">{task.dueDate}</span>
            </div>
          )}
          {task.subtasks.length > 0 && (
            <span className="text-[10px] text-muted-foreground">{task.subtasks.length} subtask{task.subtasks.length !== 1 ? "s" : ""}</span>
          )}
        </div>
      </div>
      {expanded && task.subtasks.length > 0 && (
        <div className="border-t bg-muted/20 px-3 py-2 space-y-1">
          {task.subtasks.map((st, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 shrink-0" />
              {st.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main dialog ────────────────────────────────────────────────────────────

export default function CreatePlanDialog({
  open,
  onOpenChange,
  conversationHistory,
  onPostToNotepad,
}: CreatePlanDialogProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const chatEndRef = useRef<HTMLDivElement>(null);

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [plan, setPlan] = useState<PlanData | null>(null);
  const [chatInput, setChatInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPosting, setIsPosting] = useState(false);

  // Auto-generate on open
  useEffect(() => {
    if (open && chatMessages.length === 0) {
      generatePlan([]);
    }
  }, [open]);

  // Scroll chat to bottom on new message
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setChatMessages([]);
      setPlan(null);
      setChatInput("");
    }
  }, [open]);

  const generatePlan = async (messages: ChatMessage[]) => {
    setIsLoading(true);
    try {
      const res = await fetch("/api/notes/generate-plan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversationHistory: conversationHistory,
          chatMessages: messages,
        }),
        credentials: "include",
        cache: "no-store",
      });
      if (!res.ok) throw new Error(`Failed to generate plan (${res.status})`);
      const data = await res.json();

      setChatMessages(prev => [
        ...prev,
        { role: "assistant", content: data.message ?? "Here is your plan." },
      ]);

      if (!data.needsMoreInfo && data.plan) {
        setPlan(data.plan);
      }
    } catch {
      setChatMessages(prev => [
        ...prev,
        { role: "assistant", content: "Sorry, I couldn't generate the plan right now. Please try again." },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = () => {
    const msg = chatInput.trim();
    if (!msg || isLoading) return;
    const newMessages: ChatMessage[] = [...chatMessages, { role: "user", content: msg }];
    setChatMessages(newMessages);
    setChatInput("");
    generatePlan(newMessages);
  };

  // ─── Post Plan ───────────────────────────────────────────────────────────

  // Persists the goal ID from step 1 so that a network-error retry can skip
  // re-posting the goal and resume from the task/habit/tracker loop instead.
  const planGoalIdRef = useRef<string | null>(null);
  // Idempotency keys for each task in the plan loop — keyed by index so that
  // a network-error retry reuses the same key for each slot. Cleared on
  // overall success.
  const planTaskIdempotencyKeysRef = useRef<string[]>([]);
  // A new or replaced plan is a different submission — clear any prior attempt
  // so retries of the new plan cannot reuse keys from the previous one.
  useEffect(() => {
    planGoalIdRef.current = null;
    planTaskIdempotencyKeysRef.current = [];
  }, [plan]);

  const handlePostPlan = async () => {
    if (!plan) return;
    setIsPosting(true);
    try {
      // 1. Create goal — skip if a prior attempt already succeeded.
      let goalId = planGoalIdRef.current;
      if (!goalId) {
        const goalRes = await apiRequest("POST", "/api/goals", {
          title: plan.goal.title,
          description: plan.goal.description,
          category: plan.goal.category || "General",
          priority: plan.goal.priority || "medium",
          status: "active",
          deadline: plan.goal.deadline ? new Date(plan.goal.deadline).toISOString() : undefined,
          targetValue: plan.goal.targetValue ?? undefined,
          goalSource: plan.goal.goalSource ?? undefined,
          healthMetric: plan.goal.healthMetric ?? undefined,
        });
        const goal = await goalRes.json();
        planGoalIdRef.current = goal.id;
        goalId = goal.id;
      }

      // 2. Create tasks (sequential — subtasks need task IDs).
      // Idempotency keys ensure the server deduplicates tasks from prior
      // partial attempts that used the same key.
      for (let i = 0; i < plan.tasks.length; i++) {
        const t = plan.tasks[i];
        if (!planTaskIdempotencyKeysRef.current[i]) {
          planTaskIdempotencyKeysRef.current[i] = crypto.randomUUID();
        }
        const taskRes = await apiRequest("POST", "/api/tasks", {
          title: t.title,
          description: t.description || undefined,
          category: t.category || "General",
          priority: t.priority || "medium",
          goalId,
          dueDate: t.dueDate ? new Date(t.dueDate).toISOString() : undefined,
        }, {
          "Idempotency-Key": planTaskIdempotencyKeysRef.current[i],
        });
        const task = await taskRes.json();
        for (const st of (t.subtasks ?? [])) {
          await apiRequest("POST", `/api/tasks/${task.id}/subtasks`, { text: st.text });
        }
      }

      // 3. Create habits
      await Promise.all(plan.habits.map(h =>
        apiRequest("POST", "/api/habits", {
          name: h.name,
          description: h.description || undefined,
          category: h.category || "General",
          frequency: h.frequency || "daily",
          target: h.target ?? 1,
          habitType: h.habitType || "positive",
          goalId,
        })
      ));

      // 4. Create trackers
      await Promise.all(plan.trackers.map(tr =>
        apiRequest("POST", "/api/trackerx", {
          name: tr.name,
          category: tr.category || "General",
          valueType: tr.valueType || "number",
          unitLabel: tr.unitLabel || null,
          period: tr.period || "daily",
          goalValue: tr.goalValue ?? null,
          goalId,
        })
      ));

      qc.invalidateQueries({ queryKey: ["/api/goals"] });
      qc.invalidateQueries({ queryKey: ["/api/tasks"] });
      qc.invalidateQueries({ queryKey: ["/api/habits"] });
      qc.invalidateQueries({ queryKey: ["/api/trackerx"] });

      toast({
        title: "Plan created!",
        description: `Goal "${plan.goal.title}" with ${plan.tasks.length} task${plan.tasks.length !== 1 ? "s" : ""}, ${plan.habits.length} habit${plan.habits.length !== 1 ? "s" : ""}, and ${plan.trackers.length} tracker${plan.trackers.length !== 1 ? "s" : ""} added.`,
      });
      planGoalIdRef.current = null;
      planTaskIdempotencyKeysRef.current = [];
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Failed to create plan", description: err?.message, variant: "destructive" });
    } finally {
      setIsPosting(false);
    }
  };

  // ─── Post to Notepad ─────────────────────────────────────────────────────

  const handlePostToNotepad = () => {
    if (!plan) return;

    const lines: string[] = [
      `--- Action Plan: ${plan.goal.title} ---`,
      "",
      `📋 SUMMARY`,
      plan.summary,
      "",
      `🎯 GOAL (${plan.goal.type.replace("_", " ").toUpperCase()})`,
      `Title: ${plan.goal.title}`,
      plan.goal.description ? `Description: ${plan.goal.description}` : "",
      plan.goal.deadline ? `Deadline: ${plan.goal.deadline}` : "",
      plan.goal.targetValue ? `Target: ${plan.goal.targetValue}` : "",
      plan.goal.category ? `Category: ${plan.goal.category}` : "",
      "",
    ];

    if (plan.tasks.length > 0) {
      lines.push("✅ TASKS");
      for (const t of plan.tasks) {
        lines.push(`• ${t.title}${t.dueDate ? ` (due: ${t.dueDate})` : ""}${t.priority !== "medium" ? ` [${t.priority}]` : ""}`);
        if (t.description) lines.push(`  ${t.description}`);
        for (const st of (t.subtasks ?? [])) {
          lines.push(`  - ${st.text}`);
        }
      }
      lines.push("");
    }

    if (plan.habits.length > 0) {
      lines.push("🔄 HABITS");
      for (const h of plan.habits) {
        lines.push(`• ${h.name} (${h.frequency})${h.description ? ` — ${h.description}` : ""}`);
      }
      lines.push("");
    }

    if (plan.trackers.length > 0) {
      lines.push("📊 TRACKERS");
      for (const tr of plan.trackers) {
        lines.push(`• ${tr.name}: track ${tr.unitLabel || tr.valueType} ${tr.period}${tr.goalValue ? `, goal: ${tr.goalValue} ${tr.unitLabel || ""}` : ""}`);
      }
    }

    onPostToNotepad(lines.filter(l => l !== undefined).join("\n"));
    toast({ title: "Plan posted to note" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-hidden flex flex-col p-0">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Rocket className="h-5 w-5 text-primary" />
            Create Action Plan
            {plan && (
              <Badge variant="secondary" className="ml-2 text-xs">Plan ready</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Two-panel body */}
        <div className="flex flex-1 min-h-0">
          {/* ── Left: Plan Preview ── */}
          <div className="flex flex-col w-[58%] border-r min-h-0">
            <div className="px-4 py-2 border-b bg-muted/30 shrink-0 flex items-center justify-between">
              <span className="text-sm font-semibold flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                Your Plan
              </span>
              {plan && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs gap-1"
                    onClick={handlePostToNotepad}
                  >
                    <FileText className="h-3 w-3" />
                    Post to Notepad
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs gap-1 bg-green-600 hover:bg-green-700 text-white"
                    onClick={handlePostPlan}
                    disabled={isPosting}
                  >
                    {isPosting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Rocket className="h-3 w-3" />}
                    Post Plan
                  </Button>
                </div>
              )}
            </div>

            <ScrollArea className="flex-1 p-4">
              {!plan ? (
                <div className="flex flex-col items-center justify-center h-full py-20 text-center gap-3">
                  {isLoading ? (
                    <>
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">Analyzing your conversation and building a plan…</p>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-8 w-8 text-muted-foreground/40" />
                      <p className="text-sm text-muted-foreground">Answer the AI's questions on the right to build your plan.</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Summary */}
                  <p className="text-sm text-muted-foreground italic border-l-2 border-primary/40 pl-3">{plan.summary}</p>

                  {/* Goal */}
                  <section className="space-y-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                      <Target className="h-3.5 w-3.5" /> Goal
                    </h3>
                    <div className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <GoalTypeBadge type={plan.goal.type} />
                          <p className="font-semibold text-sm mt-1">{plan.goal.title}</p>
                          {plan.goal.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">{plan.goal.description}</p>
                          )}
                        </div>
                        <PriorityBadge priority={plan.goal.priority} />
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                        {plan.goal.deadline && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> {plan.goal.deadline}
                          </span>
                        )}
                        {plan.goal.targetValue && (
                          <span className="flex items-center gap-1">
                            <Flag className="h-3 w-3" /> Target: {plan.goal.targetValue}
                          </span>
                        )}
                        {plan.goal.category && (
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-primary/50 inline-block" /> {plan.goal.category}
                          </span>
                        )}
                      </div>
                    </div>
                  </section>

                  {/* Tasks */}
                  {plan.tasks.length > 0 && (
                    <section className="space-y-2">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                        <CheckSquare className="h-3.5 w-3.5" /> Tasks ({plan.tasks.length})
                      </h3>
                      <div className="space-y-1.5">
                        {plan.tasks.map((t, i) => <TaskItem key={i} task={t} />)}
                      </div>
                    </section>
                  )}

                  {/* Habits */}
                  {plan.habits.length > 0 && (
                    <section className="space-y-2">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                        <RefreshCw className="h-3.5 w-3.5" /> Habits ({plan.habits.length})
                      </h3>
                      <div className="space-y-1.5">
                        {plan.habits.map((h, i) => (
                          <div key={i} className="flex items-start gap-2 border rounded-lg px-3 py-2">
                            <Zap className="h-3.5 w-3.5 shrink-0 mt-0.5 text-purple-500" />
                            <div>
                              <p className="text-sm font-medium">{h.name}</p>
                              {h.description && <p className="text-[11px] text-muted-foreground">{h.description}</p>}
                              <span className="text-[10px] text-muted-foreground capitalize">{h.frequency} · {h.habitType}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Trackers */}
                  {plan.trackers.length > 0 && (
                    <section className="space-y-2">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                        <BarChart2 className="h-3.5 w-3.5" /> Trackers ({plan.trackers.length})
                      </h3>
                      <div className="space-y-1.5">
                        {plan.trackers.map((tr, i) => (
                          <div key={i} className="flex items-start gap-2 border rounded-lg px-3 py-2">
                            <BarChart2 className="h-3.5 w-3.5 shrink-0 mt-0.5 text-blue-500" />
                            <div>
                              <p className="text-sm font-medium">{tr.name}</p>
                              <span className="text-[10px] text-muted-foreground">
                                {tr.valueType} · {tr.period}{tr.unitLabel ? ` · ${tr.unitLabel}` : ""}
                                {tr.goalValue ? ` · goal: ${tr.goalValue}` : ""}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  <Separator />

                  {/* Action buttons at bottom */}
                  <div className="flex gap-2 pb-2">
                    <Button
                      variant="outline"
                      className="flex-1 gap-1.5"
                      onClick={handlePostToNotepad}
                    >
                      <FileText className="h-4 w-4" />
                      Post Results to Notepad
                    </Button>
                    <Button
                      className="flex-1 gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                      onClick={handlePostPlan}
                      disabled={isPosting}
                    >
                      {isPosting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                      {isPosting ? "Creating…" : "Post Plan"}
                    </Button>
                  </div>
                </div>
              )}
            </ScrollArea>
          </div>

          {/* ── Right: AI Chat ── */}
          <div className="flex flex-col w-[42%] min-h-0">
            <div className="px-4 py-2 border-b bg-muted/30 shrink-0">
              <span className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                AI Plan Coach
              </span>
              <p className="text-[11px] text-muted-foreground mt-0.5">Ask me to adjust any part of your plan</p>
            </div>

            <ScrollArea className="flex-1 p-3">
              {chatMessages.length === 0 && isLoading && (
                <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Building your plan from the conversation…
                </div>
              )}
              <div className="space-y-3">
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                    <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                      msg.role === "assistant" ? "bg-primary/10 text-primary" : "bg-muted"
                    }`}>
                      {msg.role === "assistant" ? <Bot className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                    </div>
                    <div className={`rounded-xl px-3 py-2 text-sm max-w-[85%] ${
                      msg.role === "assistant"
                        ? "bg-muted text-foreground"
                        : "bg-primary text-primary-foreground"
                    }`}>
                      {msg.content}
                    </div>
                  </div>
                ))}
                {isLoading && chatMessages.length > 0 && (
                  <div className="flex gap-2">
                    <div className="shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                      <Bot className="h-3.5 w-3.5" />
                    </div>
                    <div className="bg-muted rounded-xl px-3 py-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            </ScrollArea>

            {/* Chat input */}
            <div className="p-3 border-t shrink-0">
              <div className="flex gap-2">
                <Textarea
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Ask me to adjust the plan, add a deadline, change tasks…"
                  className="resize-none text-sm min-h-[60px] max-h-[120px]"
                  disabled={isLoading}
                />
                <Button
                  size="icon"
                  onClick={handleSend}
                  disabled={!chatInput.trim() || isLoading}
                  className="h-auto self-end shrink-0"
                >
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5">
                Shift+Enter for new line · Enter to send
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
